// Gespeicherte Vertragsvorlagen laden - so, wie das Vertragsstudio beim
// Erstellen sie sieht. Herausgelöst aus VertragsAutomationStudio.jsx
// (23.09.2026), damit Desktop und Handy dieselbe Vorlage bekommen.
//
// Ein Stand liegt unter dem Vertragsschlüssel (CI und Druckversion teilen ihn),
// ältere Stände noch unter "schlüssel__variante". Gespeichert ist entweder der
// ganze Vertrag (content) oder nur der Quelltext des Editors
// (editorState.sourceHtml), der dann in die Originaldatei eingesetzt wird.

import axios from 'axios';
import { mergeMissingAnlage2, normalizeDachverpachtungPartyLabels } from './contractMerging';

export function buildContractStateKey(contractKey) {
  return contractKey;
}

export function buildLegacyContractStateKey(contractKey, variantKey) {
  return `${contractKey}__${variantKey}`;
}

export function hasStoredContractContent(state) {
  return Boolean(state && (state.content || state.editorState));
}

/**
 * Fehler, wenn der gespeicherte Stand einer Vorlage nicht abrufbar ist (seit 09.10.2026).
 * Vorher nahmen Studio, Bulk und Handy dann still die Originaldatei – ein Vertrag entstand
 * so auf einer alten Fassung, obwohl ein Admin eine neue gespeichert hatte (Nutzer: „immer
 * auf Basis der letzten freigegebenen Version“). Kein Stand (Antwort mit state: null) ist
 * dagegen kein Fehler: Dann gilt die Originaldatei, wie bisher.
 */
export function standNichtLadbar(fehler) {
  const grund = String(fehler?.response?.data?.detail || fehler?.message || '').trim();
  const meldung = `Die gespeicherte Vorlage konnte nicht geladen werden${grund ? ` (${grund})` : ''}. `
    + 'Ohne sie wird nichts erstellt – bitte die Seite neu laden.';
  const neu = new Error(meldung);
  neu.standNichtLadbar = true;
  return neu;
}

// Setzt den gespeicherten Editor-Quelltext in die Originaldatei ein. Die
// Seiten (#contract-pages) werden geleert - contract-pagination.js baut sie
// beim Anzeigen neu. Das VertragsStudio und das Deal-Board haben eigene,
// leicht abweichende Fassungen und bleiben bewusst dabei.
export function applySourceHtmlToTemplate(baseHtml, sourceHtml) {
  if (typeof baseHtml !== 'string' || !baseHtml) return baseHtml;
  if (typeof sourceHtml !== 'string') return baseHtml;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(baseHtml, 'text/html');
    const source = doc.getElementById('contract-source');
    if (!source) return baseHtml;
    source.innerHTML = sourceHtml;
    const generatedPages = doc.getElementById('contract-pages');
    if (generatedPages) generatedPages.innerHTML = '';
    return '<!doctype html>\n' + doc.documentElement.outerHTML;
  } catch (error) {
    console.error('applySourceHtmlToTemplate failed:', error);
    return baseHtml;
  }
}

async function leseStand({ apiBase, authHeaders, schluessel }) {
  const antwort = await axios.get(`${apiBase}/contracts/state`, {
    params: { contract_key: schluessel },
    headers: authHeaders,
  });
  return antwort.data?.state || null;
}

/**
 * Lädt die Vorlage einer Vertragsvariante: gespeicherter Stand, sonst die
 * Originaldatei aus public/contracts. Fehlt beides oder ist der Stand nicht
 * abrufbar (Fehler statt „kein Stand“), wird geworfen.
 *
 * @param {{ apiBase: string, authHeaders: object, contractKey: string,
 *   variant: { key: string, file: string }, isAuthenticated?: boolean }} optionen
 * @returns {Promise<{ html: string, source: 'original'|'sourceHtml'|'savedContent'|'syncedFromSibling' }>}
 */
export async function ladeVertragsVorlage({ apiBase, authHeaders, contractKey, variant, isAuthenticated = true }) {
  const stateKey = buildContractStateKey(contractKey);
  const legacyKey = buildLegacyContractStateKey(contractKey, variant.key);
  const originalPromise = fetch(`/contracts/${variant.file}`).then((r) => (r.ok ? r.text() : ''));
  let html = null;
  let source = 'original';

  if (isAuthenticated) {
    try {
      let state = await leseStand({ apiBase, authHeaders, schluessel: stateKey });
      let stateSource = 'canonical';
      if (!hasStoredContractContent(state) && legacyKey !== stateKey) {
        try {
          const legacyState = await leseStand({ apiBase, authHeaders, schluessel: legacyKey });
          if (hasStoredContractContent(legacyState)) {
            state = legacyState;
            stateSource = 'legacy';
          }
        } catch (_legacyError) {
          // Der alte Schlüssel ist nur ein optionaler Rückgriff.
        }
      }
      const sourceHtml = typeof state?.editorState?.sourceHtml === 'string' ? state.editorState.sourceHtml : '';
      if (sourceHtml && stateSource === 'canonical') {
        const baseHtml = await originalPromise;
        if (baseHtml) {
          html = applySourceHtmlToTemplate(baseHtml, sourceHtml);
          source = 'sourceHtml';
        }
      } else if (state?.content) {
        html = state.content;
        source = 'savedContent';
      } else if (sourceHtml) {
        const baseHtml = await originalPromise;
        if (baseHtml) {
          html = applySourceHtmlToTemplate(baseHtml, sourceHtml);
          source = 'syncedFromSibling';
        }
      }
    } catch (fehler) {
      // Nicht mehr still die Originaldatei (seit 09.10.2026), siehe standNichtLadbar.
      throw standNichtLadbar(fehler);
    }
  }

  const originalHtml = await originalPromise;
  if (!html) {
    if (!originalHtml) throw new Error('Vorlage nicht gefunden.');
    html = originalHtml;
  } else if (originalHtml) {
    // Fehlende Abschnitte (z. B. Anlage 2) aus dem aktuellen Original
    // ergänzen, ohne den gespeicherten Stand sonst zu verändern.
    html = mergeMissingAnlage2(html, originalHtml);
  }

  return { html: normalizeDachverpachtungPartyLabels(html, contractKey), source };
}

// Überschriften der Paragraphen (h2.section-heading) als Text - für das
// Gespräch mit der KI ("Wo gehört der Passus hin?"). Entitäten müssen
// dekodiert werden: In den Vorlagen steht "&#167; 13 Zusatzvereinbarung",
// gesucht wird aber "§ 13 Zusatzvereinbarung". Das textarea ist dafür der
// harmlose Weg - es liest nur Text heraus und serialisiert nichts zurück.
export function vertragsAbschnitteAus(html) {
  const text = String(html || '');
  if (!text) return [];

  const entschluessler = typeof document !== 'undefined' ? document.createElement('textarea') : null;
  const alsText = (roh) => {
    const ohneTags = String(roh || '').replace(/<[^>]+>/g, ' ');
    if (!entschluessler) return ohneTags.replace(/\s+/g, ' ').trim();
    entschluessler.innerHTML = ohneTags;
    return String(entschluessler.value || '').replace(/\s+/g, ' ').trim();
  };

  const gefunden = [];
  const muster = /<h2\b([^>]*)>([\s\S]*?)<\/h2>/gi;
  let treffer;
  while ((treffer = muster.exec(text))) {
    if (!/\bsection-heading\b/i.test(treffer[1] || '')) continue;
    const titel = alsText(treffer[2]);
    if (titel && !gefunden.includes(titel)) gefunden.push(titel);
  }
  return gefunden;
}

// Für die Anzeige in einem iframe: Relative Pfade der Vorlagen (Skripte,
// Bilder) zeigen auf public/contracts.
export function injectBaseHref(html, baseHref) {
  if (typeof html !== 'string' || !html) return html;
  // Ein auskommentiertes <base> zählt nicht (Chrome „Seite speichern unter“, 25.10.2026).
  if (/<base\s/i.test(html.replace(/<!--[\s\S]*?-->/g, ''))) return html;
  const baseTag = `<base href="${baseHref}">`;
  if (/<head([^>]*)>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
  return html;
}

// Nur lesen: Editor-Leiste und contenteditable der Vorlage abschalten.
export function disableEditorInHtml(html) {
  if (typeof html !== 'string' || !html) return html;
  const block = '<style id="enpower-automation-readonly">'
    + '.enpower-editor-panel { display: none !important; }'
    + 'body.editor-mode, body.editor-move-mode { /* Schutz: Editor-Modus deaktivieren */ }'
    + 'body.editor-mode [contenteditable="true"], body.editor-move-mode [contenteditable="true"] { -webkit-user-modify: read-only; }'
    + '</style>'
    + '<script>(function(){function disable(){var p=document.querySelector(".enpower-editor-panel");if(p)p.remove();document.querySelectorAll("[contenteditable]").forEach(function(n){n.setAttribute("contenteditable","false");});document.body&&document.body.classList.remove("editor-mode","editor-move-mode");}if(document.readyState==="loading"){document.addEventListener("DOMContentLoaded",function(){setTimeout(disable,30);});}else{setTimeout(disable,30);}setTimeout(disable,400);})();</script>';
  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${block}</head>`);
  if (/<head([^>]*)>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${block}`);
  return block + html;
}

// ---------------------------------------------------------------------------
// Was in einem Vertrag steht: Platzhalter, Anlagenverzeichnis, Lageplan.
// Herausgelöst aus VertragsAutomationStudio.jsx (23.09.2026), damit das
// Erstellen am Handy dieselben Stellen findet wie das Studio am Desktop.
// ---------------------------------------------------------------------------

export const CONTRACTS_AM_URL = process.env.REACT_APP_CONTRACTS_AM_URL
  || 'https://gjouokpgdwlcdjdenpdt.supabase.co/functions/v1/contracts-am';
export const LAGEPLAN_PLACEHOLDER = 'Lageplan_Bild';
export const LAGEPLAN_PLACEHOLDER_TOKEN = `{{${LAGEPLAN_PLACEHOLDER}}}`;

export function decodeHtmlEntities(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/&#(\d+);/g, (_m, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&auml;/g, 'ä').replace(/&ouml;/g, 'ö').replace(/&uuml;/g, 'ü')
    .replace(/&Auml;/g, 'Ä').replace(/&Ouml;/g, 'Ö').replace(/&Uuml;/g, 'Ü')
    .replace(/&szlig;/g, 'ß').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ');
}

export function extractPlaceholders(html) {
  if (typeof html !== 'string') return [];
  const matches = html.match(/\{\{[^}]+\}\}/g) || [];
  const decoded = matches.map((m) => decodeHtmlEntities(m.slice(2, -2).trim()));
  return Array.from(new Set(decoded)).sort((a, b) => a.localeCompare(b, 'de'));
}

export function normalizePlaceholderName(value) {
  return decodeHtmlEntities(String(value || ''))
    .replace(/[｛]/g, '{')
    .replace(/[｝]/g, '}')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[{}]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function collectPlaceholdersFromText(value, placeholders) {
  const text = String(value || '')
    .replace(/[｛]/g, '{')
    .replace(/[｝]/g, '}');
  text.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, key) => {
    const normalized = normalizePlaceholderName(key);
    if (normalized) placeholders.add(normalized);
    return _match;
  });
}

export function plainTextFromHtml(value) {
  const html = String(value || '');
  if (!html || typeof document === 'undefined') return '';
  const container = document.createElement('div');
  container.innerHTML = html;
  return container.textContent || '';
}

export function extractClausePlaceholders(content) {
  const placeholders = new Set();
  const raw = String(content || '');
  const decoded = decodeHtmlEntities(raw);
  collectPlaceholdersFromText(raw, placeholders);
  collectPlaceholdersFromText(decoded, placeholders);
  collectPlaceholdersFromText(plainTextFromHtml(decoded), placeholders);
  return Array.from(placeholders).sort((a, b) => a.localeCompare(b, 'de'));
}

export function mergePlaceholders(...groups) {
  const keys = new Set();
  groups.flat().forEach((placeholder) => {
    const key = normalizePlaceholderName(placeholder);
    if (key) keys.add(key);
  });
  return Array.from(keys).sort((a, b) => a.localeCompare(b, 'de'));
}

export function extractAnnexReferencesFromHtml(html) {
  if (typeof html !== 'string' || !html.trim() || typeof DOMParser === 'undefined') return [];
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const heading = Array.from(doc.querySelectorAll('h1,h2,h3,h4,.section-heading'))
      .find((node) => /anlagenverzeichnis/i.test(node.textContent || ''));
    const container = heading?.nextElementSibling;
    const rows = container
      ? Array.from(container.querySelectorAll('p,li,.bullet-line'))
      : Array.from(doc.querySelectorAll('.annex-list p, .annex-list li, .annex-list .bullet-line'));
    const seen = new Set();
    return rows.map((row, index) => {
      const spans = Array.from(row.querySelectorAll('span'));
      const first = (spans[0]?.textContent || '').trim();
      const second = spans.length > 1 ? spans.slice(1).map((span) => span.textContent || '').join(' ').trim() : '';
      const text = (second || row.textContent || '').replace(/\s+/g, ' ').trim();
      const numberMatch = first.match(/\d+/) || text.match(/^(?:Anlage\s*)?(\d+)[.)\s-]+/i);
      // Projektrechte (seit 26.10.2026): Die Nummer zaehlt die Vorlage per CSS,
      // die erste Zelle ist leer - dann gilt die Position in der Liste.
      const zaehltPerCss = !numberMatch && spans.length > 1 && !first;
      const anlageNumber = numberMatch ? Number(numberMatch[0].replace(/\D/g, '')) : (zaehltPerCss ? index + 1 : null);
      const label = text.replace(/^(?:Anlage\s*)?\d+[.)\s-]+/i, '').trim();
      if (!anlageNumber || !label || seen.has(anlageNumber)) return null;
      seen.add(anlageNumber);
      return { id: `annex-${anlageNumber}`, anlageNumber, label };
    }).filter(Boolean);
  } catch (_error) {
    return [];
  }
}

export function findLageplanImageTarget(doc) {
  if (!doc) return { container: null, image: null };
  const root = doc.getElementById?.('contract-source') || doc;
  const container = root.querySelector('.anlage2-page-mapbox')
    || root.querySelector('section.anlage2-page')
    || root.querySelector('[data-anlage="2"]');
  const imageInContainer = container?.querySelector?.('img') || null;
  if (imageInContainer) return { container, image: imageInContainer };

  const imageByAlt = Array.from(root.querySelectorAll('img')).find((image) => (
    /lageplan/i.test(image.getAttribute('alt') || '')
  )) || null;
  return { container: container || imageByAlt?.parentElement || null, image: imageByAlt };
}

export function getLageplanImageInfo(html) {
  if (typeof html !== 'string' || !html) return { available: false, hasImage: false, source: '' };
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const target = findLageplanImageTarget(doc);
    const source = target.image?.getAttribute('src') || '';
    return {
      available: Boolean(target.container || target.image),
      hasImage: Boolean(source),
      source,
      isPlaceholder: source.includes(LAGEPLAN_PLACEHOLDER_TOKEN),
      isEmbedded: /^data:image\//i.test(source),
    };
  } catch (_error) {
    return { available: false, hasImage: false, source: '' };
  }
}

export function replaceLageplanImageSource(html, source, { fileName = '', dynamic = false } = {}) {
  if (typeof html !== 'string' || !html.trim()) {
    throw new Error('Kein HTML-Inhalt vorhanden.');
  }
  const nextSource = String(source || '').trim();
  if (!nextSource) {
    throw new Error('Kein Lageplan-Bild vorhanden.');
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const target = findLageplanImageTarget(doc);
  if (!target.container && !target.image) {
    throw new Error('Im Vertrag wurde kein Lageplan-Bereich gefunden.');
  }

  let image = target.image;
  if (!image) {
    const empty = target.container.querySelector?.('.anlage2-page-empty') || null;
    image = doc.createElement('img');
    if (empty) {
      empty.replaceWith(image);
    } else {
      target.container.appendChild(image);
    }
  }

  image.setAttribute('src', nextSource);
  image.setAttribute('alt', 'Vorl\u00e4ufiger Lageplan');
  image.setAttribute('loading', 'eager');
  if (dynamic) {
    image.setAttribute('data-enpower-lageplan-mapping', LAGEPLAN_PLACEHOLDER);
    image.setAttribute('onerror', "this.outerHTML='<span class=\"anlage2-page-empty\"><strong>Lageplan-Bild aus Bitrix konnte nicht geladen werden</strong></span>'");
  } else {
    image.removeAttribute('data-enpower-lageplan-mapping');
    image.removeAttribute('onerror');
  }
  if (fileName) image.setAttribute('data-enpower-lageplan-filename', fileName);

  const generatedPages = doc.getElementById('contract-pages');
  if (generatedPages) generatedPages.innerHTML = '';
  return '<!doctype html>\n' + doc.documentElement.outerHTML;
}

export function readImageFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Bilddatei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });
}

// Setzt den Vertragstext einer hochgeladenen (bearbeiteten) Fassung in die
// Vorlage der anderen Variante ein - so entsteht die Druckversion aus der
// bearbeiteten CI-Fassung und umgekehrt.
export function applyUploadedContractToVariantTemplate(baseHtml, uploadedHtml) {
  if (typeof baseHtml !== 'string' || !baseHtml) return baseHtml;
  if (typeof uploadedHtml !== 'string' || !uploadedHtml) return baseHtml;
  try {
    const parser = new DOMParser();
    const baseDoc = parser.parseFromString(baseHtml, 'text/html');
    const uploadedDoc = parser.parseFromString(uploadedHtml, 'text/html');
    const baseSource = baseDoc.getElementById('contract-source');
    const uploadedSource = uploadedDoc.getElementById('contract-source');
    if (!baseSource || !uploadedSource) return baseHtml;

    baseSource.innerHTML = uploadedSource.innerHTML;
    const generatedPages = baseDoc.getElementById('contract-pages');
    if (generatedPages) generatedPages.innerHTML = '';
    return '<!doctype html>\n' + baseDoc.documentElement.outerHTML;
  } catch (error) {
    console.error('applyUploadedContractToVariantTemplate failed:', error);
    return baseHtml;
  }
}

/**
 * Vorlage eines ANDEREN Vertrags laden -- fuer Anlagen, die selbst ein Vertrag
 * sind. Bewusst derselbe Weg wie beim eigenen Vertrag (gespeicherter Stand vor
 * Originalvorlage), damit die Anlage aussieht wie der eigenstaendige Vertrag
 * und nicht wie eine zweite, eigene Fassung.
 */
export async function loadAnnexContractVariants({ apiBase, authHeaders, isAuthenticated, contract }) {
  const annexStateKey = buildContractStateKey(contract.key);
  return Promise.all(contract.variants.map(async (variant) => {
    const originalPromise = fetch(`/contracts/${variant.file}`).then((r) => (r.ok ? r.text() : ''));
    let html = null;

    if (isAuthenticated) {
      try {
        const stateResp = await axios.get(`${apiBase}/contracts/state`, {
          params: { contract_key: annexStateKey },
          headers: authHeaders,
        });
        let state = stateResp.data?.state;
        const legacyStateKey = buildLegacyContractStateKey(contract.key, variant.key);
        if (!hasStoredContractContent(state) && legacyStateKey !== annexStateKey) {
          try {
            const legacyResp = await axios.get(`${apiBase}/contracts/state`, {
              params: { contract_key: legacyStateKey },
              headers: authHeaders,
            });
            const legacyState = legacyResp.data?.state || null;
            if (hasStoredContractContent(legacyState)) state = legacyState;
          } catch (_legacyError) {
            // Legacy-Stand ist nur lesend und optional.
          }
        }
        const sourceHtml = typeof state?.editorState?.sourceHtml === 'string' ? state.editorState.sourceHtml : '';
        if (sourceHtml) {
          const baseHtml = await originalPromise;
          if (baseHtml) html = applySourceHtmlToTemplate(baseHtml, sourceHtml);
        } else if (state?.content) {
          html = state.content;
        }
      } catch (fehler) {
        // Kein Stand bleibt die Originalvorlage; ein nicht abrufbarer nicht (09.10.2026).
        throw standNichtLadbar(fehler);
      }
    }

    const originalHtml = await originalPromise;
    if (!html) {
      if (!originalHtml) throw new Error(`Vorlage "${variant.file}" wurde nicht gefunden.`);
      html = originalHtml;
    } else if (originalHtml) {
      html = mergeMissingAnlage2(html, originalHtml);
    }

    return { variant, html: normalizeDachverpachtungPartyLabels(html, contract.key) };
  }));
}

/** Feldzuordnung des Anlagen-Vertrags: sie kennt Platzhalter, die im Hauptvertrag fehlen. */
export async function loadAnnexContractMappingFields({ authHeaders, isAuthenticated, contractKey }) {
  if (!isAuthenticated || !contractKey) return {};
  try {
    const response = await axios.get(CONTRACTS_AM_URL, {
      params: { op: 'mapping', contract_key: buildContractStateKey(contractKey) },
      headers: authHeaders,
    });
    const fields = response.data?.mapping?.fields;
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return {};
    return fields;
  } catch (error) {
    console.warn('Mapping der Vertragsanlage konnte nicht geladen werden:', contractKey, error);
    return {};
  }
}

// ---------------------------------------------------------------------------
// Erzeugen und Hochladen: Käuferfelder, Paragraphen-Zuordnung, Anlagen,
// weitere Eigentümer, Upload-Warteschlange. Herausgelöst aus
// VertragsAutomationStudio.jsx (23.09.2026) - Studio und Handy bauen die
// Nutzlast damit gleich.
// ---------------------------------------------------------------------------

export const ACTIVE_MANUAL_UPLOAD_BATCH_STORAGE_KEY = 'enpower:contracts-am:activeManualUploadBatch:v1';

export const MAX_CONTRACT_ATTACHMENT_BYTES = 12 * 1024 * 1024;
export const BUYER_FIELD_PLACEHOLDERS = [
  'Firma Käufer',
  'Straße Käufer',
  'PLZ Ort Käufer',
  'Registergericht Käufer',
  'HRB Käufer',
  'Vertretungsberechtigte Käufer',
  'Telefon Käufer',
  'Mobil Käufer',
  'E-Mail Käufer',
  'Email Käufer',
  'USt-ID Käufer',
  'Umsatzsteuer-ID Käufer',
  'Steuernummer Käufer',
  'Geschäftsform Käufer',
  'Sitz Käufer',
];

export function normalizeBuyerDraft(value = {}) {
  return {
    id: String(value.id || '').trim(),
    label: String(value.label || value.name || value.company || '').trim(),
    company: String(value.company || '').trim(),
    street: String(value.street || '').trim(),
    postalCity: String(value.postalCity || value.zipCity || '').trim(),
    registerCourt: String(value.registerCourt || '').trim(),
    hrb: String(value.hrb || '').trim(),
    representatives: String(value.representatives || value.representedBy || '').trim(),
    phone: String(value.phone || value.telefon || '').trim(),
    mobile: String(value.mobile || value.mobil || '').trim(),
    email: String(value.email || '').trim(),
    vatId: String(value.vatId || value.ustId || value.ustid || '').trim(),
    taxNumber: String(value.taxNumber || value.steuernummer || '').trim(),
    legalForm: String(value.legalForm || value.geschaeftsform || value.geschäftsform || '').trim(),
    seat: String(value.seat || value.sitz || '').trim(),
  };
}

export function hasBuyerValues(buyer) {
  const normalized = normalizeBuyerDraft(buyer);
  return Boolean(
    normalized.company
    || normalized.street
    || normalized.postalCity
    || normalized.registerCourt
    || normalized.hrb
    || normalized.representatives
  );
}

export function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function replacePlaceholderWithManualValue(html, placeholder, value) {
  const cleaned = String(value || '').trim();
  if (!cleaned || typeof html !== 'string') return html;
  const pattern = new RegExp(`\\{\\{\\s*${escapeRegExp(placeholder)}\\s*\\}\\}`, 'g');
  return html.replace(pattern, escapeHtml(cleaned));
}

export function buildBuyerManualFields(buyer) {
  const normalized = normalizeBuyerDraft(buyer);
  return {
    'Firma Käufer': normalized.company,
    'Straße Käufer': normalized.street,
    'PLZ Ort Käufer': normalized.postalCity,
    'Registergericht Käufer': normalized.registerCourt,
    'HRB Käufer': normalized.hrb,
    'Vertretungsberechtigte Käufer': normalized.representatives,
    'Telefon Käufer': normalized.phone,
    'Mobil Käufer': normalized.mobile,
    'E-Mail Käufer': normalized.email,
    'Email Käufer': normalized.email,
    'USt-ID Käufer': normalized.vatId,
    'Umsatzsteuer-ID Käufer': normalized.vatId,
    'Steuernummer Käufer': normalized.taxNumber,
    'Geschäftsform Käufer': normalized.legalForm,
    'Sitz Käufer': normalized.seat,
  };
}

export function applyBuyerManualFieldsToHtml(html, buyer, enabled = true) {
  if (!enabled || !hasBuyerValues(buyer) || typeof html !== 'string') return html;
  let nextHtml = html;
  Object.entries(buildBuyerManualFields(buyer)).forEach(([placeholder, value]) => {
    nextHtml = replacePlaceholderWithManualValue(nextHtml, placeholder, value);
  });
  return nextHtml;
}

export function removeBuyerFieldsFromMapping(fields, enabled = true) {
  if (!enabled || !fields || typeof fields !== 'object') return fields || {};
  const buyerSet = new Set(BUYER_FIELD_PLACEHOLDERS);
  return Object.fromEntries(Object.entries(fields).filter(([placeholder]) => !buyerSet.has(normalizePlaceholderName(placeholder))));
}

export function normalizeClauseDefaultMapping(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result = {};
  Object.entries(value).forEach(([placeholder, rawMapping]) => {
    const key = normalizePlaceholderName(placeholder);
    const mapping = String(rawMapping || '').trim();
    if (key && mapping) result[key] = mapping;
  });
  return result;
}

export function buildMappingFieldsWithClauseDefaults(mappingDraft, selectedClauses) {
  const next = {};
  (Array.isArray(selectedClauses) ? selectedClauses : []).forEach((clause) => {
    const defaults = normalizeClauseDefaultMapping(clause?.default_mapping || clause?.defaultMapping);
    Object.entries(defaults).forEach(([placeholder, mapping]) => {
      if (placeholder && mapping && !String(next[placeholder] || '').trim()) {
        next[placeholder] = mapping;
      }
    });
  });
  Object.entries(mappingDraft || {}).forEach(([placeholder, mapping]) => {
    if (String(mapping || '').trim()) {
      next[placeholder] = mapping;
    } else if (!Object.prototype.hasOwnProperty.call(next, placeholder)) {
      next[placeholder] = mapping;
    }
  });
  return next;
}

export function createAttachmentId() {
  return `attachment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.replace(/^data:[^;]+;base64,/i, ''));
    };
    reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });
}

export function normalizeAttachmentForPayload(attachment) {
  if (!attachment?.base64 || !attachment?.filename) return null;
  return {
    type: 'uploaded-file',
    key: attachment.id || createAttachmentId(),
    label: String(attachment.label || attachment.filename || 'Anlage').trim(),
    anlageNumber: Number(attachment.anlageNumber || 0) || null,
    filename: String(attachment.filename || '').trim(),
    mimeType: String(attachment.mimeType || '').trim(),
    base64: attachment.base64,
    bytes: Number(attachment.bytes || 0) || 0,
  };
}

export function rememberManualUploadBatch(batchId) {
  const id = String(batchId || '').trim();
  if (!id || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACTIVE_MANUAL_UPLOAD_BATCH_STORAGE_KEY, id);
    window.dispatchEvent(new CustomEvent('enpower-contract-manual-upload-queued', { detail: { batchId: id } }));
  } catch (_error) {
    // Queue liegt serverseitig; localStorage ist nur Komfort für die Anzeige.
  }
}

export function kickManualUploadWorker(apiBase, authHeaders) {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  try {
    window.fetch(`${apiBase}/contracts/usage?op=am-upload-queue-worker&max_jobs=1`, {
      method: 'GET',
      headers: authHeaders || {},
      cache: 'no-store',
      keepalive: true,
    }).catch(() => {});
  } catch (_error) {
    // Der Auftrag bleibt serverseitig in der Queue und wird beim nächsten Poll erneut angestoßen.
  }
}

export function createAdditionalOwnerDraft() {
  return {
    id: `owner-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    company: '',
    name: '',
    street: '',
    postalCity: '',
    email: '',
    phone: '',
  };
}

export function cleanAdditionalOwnersForPayload(owners) {
  return (Array.isArray(owners) ? owners : [])
    .map((owner) => ({
      company: String(owner?.company || '').trim(),
      name: String(owner?.name || '').trim(),
      street: String(owner?.street || '').trim(),
      postalCity: String(owner?.postalCity || '').trim(),
      email: String(owner?.email || '').trim(),
      phone: String(owner?.phone || '').trim(),
    }))
    .filter((owner) => Object.values(owner).some(Boolean));
}
